// Hero puck materials — the one gold thing on the ice. White-hot top face,
// gold ring sides (matches the puck reference render), an additive motion
// streak, and a soft blob shadow that sells the Shot-mode lift.

import { mix, normalLocal, smoothstep, uniform, uv, vec3 } from "three/tsl";
import { AdditiveBlending, Color, DoubleSide, MeshBasicNodeMaterial } from "three/webgpu";
import type { EntitiesStyle } from "../style/style-schema.js";

function buildUniforms(style: EntitiesStyle) {
  return {
    coreColor: uniform(new Color(style.puck.coreColor)),
    ringColor: uniform(new Color(style.puck.ringColor)),
    coreIntensity: uniform(style.puck.coreIntensity),
    ringIntensity: uniform(style.puck.ringIntensity),
    streakColor: uniform(new Color(style.puck.streakColor)),
    streakIntensity: uniform(style.puck.streakIntensity),
    /** Per-frame: streak alpha (speed-driven). */
    streakAlpha: uniform(0),
    shadowAlpha: uniform(style.puck.shadowAlpha),
    /** FX: shot-released heat spike, decays to 0. */
    heat: uniform(0),
  };
}

export type PuckUniforms = ReturnType<typeof buildUniforms>;

export function applyPuckStyle(u: PuckUniforms, s: EntitiesStyle): void {
  u.coreColor.value.set(s.puck.coreColor);
  u.ringColor.value.set(s.puck.ringColor);
  u.coreIntensity.value = s.puck.coreIntensity;
  u.ringIntensity.value = s.puck.ringIntensity;
  u.streakColor.value.set(s.puck.streakColor);
  u.streakIntensity.value = s.puck.streakIntensity;
  u.shadowAlpha.value = s.puck.shadowAlpha;
}

export function createPuckMaterials(style: EntitiesStyle) {
  const u = buildUniforms(style);

  // Body: cylinder — top face (normal.y ≈ 1) is the white-hot core, the
  // side wall is the gold ring. `heat` lifts both on shot release.
  const body = new MeshBasicNodeMaterial();
  {
    const topMask = smoothstep(0.5, 0.9, normalLocal.y);
    const color = mix(u.ringColor.mul(u.ringIntensity), u.coreColor.mul(u.coreIntensity), topMask);
    body.colorNode = color.mul(u.heat.add(1));
  }

  // Streak: stretched additive quad behind the puck (same geometry recipe
  // as skater trails), fading toward the tail.
  const streak = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
  });
  {
    const fade = uv().y.oneMinus();
    streak.colorNode = u.streakColor.mul(u.streakIntensity);
    streak.opacityNode = fade.mul(fade).mul(u.streakAlpha);
  }

  // Blob shadow decal: radial falloff disc, scales/offsets with lift.
  const shadow = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  {
    const r = uv().sub(0.5).mul(2).length();
    shadow.colorNode = vec3(0, 0, 0);
    shadow.opacityNode = smoothstep(1.0, 0.2, r).mul(u.shadowAlpha);
  }

  return { u, body, streak, shadow };
}
